import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { RadioButton } from "primereact/radiobutton";
import { TabView, TabPanel } from "primereact/tabview";
import { Image } from "primereact/image";
import { FaCheck } from "react-icons/fa6";
import { Dialog } from "primereact/dialog";
import Slider from "@mui/material/Slider";
import useStoreHealthPlan from "../../../store/actions/store-health-plan-quote";
import useGetCarPlan from "../../../store/selectors/get-health-plan-quote";

import useGetQuoteHealthDetails from "../../../store/selectors/get-quote-health-details";
import { getHealthQuotes, getHealthQuotesFilterVariables } from "../../../utils/backend";
import useGetToken from "../../../store/selectors/get-token";
import FloatingSelect from "../../../components/FloatingSelect";
import BottomMessagePopUp from "../../../components/BottomMessagePopUp";
import { useTranslation } from 'react-i18next';

import Loader from "../../../components/Loader";

const SelectHealthPlan = () => {
	const { t } = useTranslation();
	const [plan, setPlan] = useState("");
	const [plans, setPlans] = useState([]);
	const navigate = useNavigate();
	const [errorMessage, setErrorMessage] = useState("");
	const [isErrorVisible, setIsErrorVisible] = useState(false);
	const showErrorMessage = (msg) => {
		setErrorMessage(msg);
		setIsErrorVisible(true);
	};
	const quoteHealthDetails = useGetQuoteHealthDetails();
	const storeHealthPlan = useStoreHealthPlan();
	const token = useGetToken();
	const [loading, setLoading] = useState(false);
	const getHealthPlan = useGetCarPlan();

	const plansPerPage = 5;

	const [filters, setFilters] = useState({
		preHospitalisation: "",
		postHospitalisation: "",
		annualLimitRange: ['', ''],
		lifetimeLimitRange: ['', ''],
		selectedProviders: [],
		isTakaful: false,
		currentPage: 1,
		itemsPerPage: plansPerPage
	});
	
	const [filterVariables, setFilterVariables] = useState({
		minLifetimeLimit: "",
		maxLifetimeLimit: "",
		minAnnualLimit: "",
		maxAnnualLimit: "",
		providers: []
	});

	const [activeTabIndex, setActiveTabIndex] = useState(0);
	const [tempSelectedProviders, setTempSelectedProviders] = useState([]);
	const [isModalVisible, setIsModalVisible] = useState(false);
	const [tagsVisible, setTagsVisible] = useState(false);
	const [selectedValueType, setSelectedValueType] = useState("");
	const [tempPriceRangeAnnual, setTempPriceRangeAnnual] = useState([null, null]);
	const [tempPriceRangeLifetime, setTempPriceRangeLifetime] = useState([null, null]);
	const [isLoading, setIsLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const observer = useRef();

	const hospitalisationOptions = [
		{ value: "", label: t("common.pleaseSelect") },
		{ value: "30", label: `30 ${t("healthInsurance.selectHealthPlan.filters.days")}` },
		{ value: "60", label: `60 ${t("healthInsurance.selectHealthPlan.filters.days")}` },
		{ value: "90", label: `90 ${t("healthInsurance.selectHealthPlan.filters.days")}` },
	];

	const lastPlanElementRef = useCallback((node) => {
		if (isLoading) return;
		
		if (observer.current) {
			observer.current.disconnect();
		}
		
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore) {
				handlePageChange(filters.currentPage + 1);
			}
		});
		
		if (node) {
			observer.current.observe(node);
		}
	}, [isLoading, hasMore, filters.currentPage]);

	const fetchHealthPlan = async () => {
		setIsLoading(true);
		try {
			const payload = {
				gender: quoteHealthDetails.gender,
				room_board: quoteHealthDetails.room_board,
				is_smoker: quoteHealthDetails.is_smoker,
				is_dangerous_expose: quoteHealthDetails.is_dangerous_expose,
				age: quoteHealthDetails.age,
				pre_hospitalisation: filters.preHospitalisation,
				post_hospitalisation: filters.postHospitalisation,
				page: filters.currentPage,
				perPage: filters.itemsPerPage,
				company_name: filters.selectedProviders,
				annual_limit_min: filters.annualLimitRange[0],
				annual_limit_max: filters.annualLimitRange[1],
				lifetime_limit_min: filters.lifetimeLimitRange[0],
				lifetime_limit_max: filters.lifetimeLimitRange[1],
				is_takaful: filters.isTakaful
			};
			
			const response = await getHealthQuotes(token, payload);
			if (response.success) {
				// Append new plans to existing ones instead of replacing
				setPlans(prevPlans => {
					if (filters.currentPage === 1) {
						return response?.data?.data || [];
					}
					return [...prevPlans, ...(response?.data?.data || [])];
				});

				setHasMore(response?.data?.hasNextPage || false);
			} else {
				showErrorMessage(`${t("messages.failedToAuthenticateToken")}`);
			}
		} catch (err) {
			showErrorMessage(`${t("messages.failedToFetchHealthPlan")}\n` + err.message);
		} finally {
			setIsLoading(false);
		}
	};
	
	const fetchHealthPlanFilters = async () => {
		setLoading(true);
		try {
			const response = await getHealthQuotesFilterVariables(token);
			if (response.success) {
				const data = response?.data || {};
				setFilterVariables({
					minLifetimeLimit: parseFloat(data?.lifetime_limit?.min),
					maxLifetimeLimit: parseFloat(data?.lifetime_limit?.max),
					minAnnualLimit: parseFloat(data?.annual_limit?.min),
					maxAnnualLimit: parseFloat(data?.annual_limit?.max),
					providers: data?.companies || []
				})
				setTempPriceRangeAnnual([parseFloat(data?.annual_limit?.min), parseFloat(data?.annual_limit?.max)])
				setTempPriceRangeLifetime([parseFloat(data?.lifetime_limit?.min), parseFloat(data?.lifetime_limit?.max)]);
			} else {
				showErrorMessage(`${t("messages.failedToAuthenticateToken")}`);
			}
		} catch (err) {
			showErrorMessage(`${t("messages.failedToFetchHealthPlan")}\n` + err.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchHealthPlan();
	}, [filters, quoteHealthDetails]);
	
	useEffect(() => {
		fetchHealthPlanFilters();
	}, []);

	useEffect(() => {
		if (getHealthPlan) {
			setPlan(getHealthPlan);
		}
	}, [getHealthPlan]);

	const handlePreHospitalisationSelectChange = (event) => {
		setFilters(prev => ({
			...prev,
			preHospitalisation: event.target.value
		}));
	};

	const handlePostHospitalisationSelectChange = (event) => {
		setFilters(prev => ({
			...prev,
			postHospitalisation: event.target.value
		}));
	};

	const handlePageChange = (newPage) => {
		setFilters(prev => ({
			...prev,
			currentPage: newPage
		}));
	};

	const handleSwitchTab = (e) => {
		setActiveTabIndex(e.index);
		setFilters(prev => ({
			...prev,
			isTakaful: e.index === 1
		}));
	};

	const handleSliderChangeAnnual = (event, newValue) => {
		if (Array.isArray(newValue) && newValue.every((v) => typeof v === "number")) {
			setTempPriceRangeAnnual(newValue);
		} else {
			console.error("Invalid Slider Value:", newValue);
		}
	};

	const handleSliderChangeLifetime = (event, newValue) => {
		if (Array.isArray(newValue) && newValue.every((v) => typeof v === "number")) {
			setTempPriceRangeLifetime(newValue);
		} else {
			console.error("Invalid Slider Value:", newValue);
		}
	};

	const submitSelectedPlan = () => {
		storeHealthPlan(plan);
		navigate(`/get-health-quotes/questionnaires`);
	};

	const openFilterModal = () => {
		setIsModalVisible(true);
	};

	const closeFilterModal = () => {
		setIsModalVisible(false);
	};

	const handleApplyFilter = () => {
		setFilters(prev => ({
			...prev,
			selectedProviders: tempSelectedProviders,
			annualLimitRange: tempPriceRangeAnnual,
			lifetimeLimitRange: tempPriceRangeLifetime
		}));
		setTagsVisible(true);
		closeFilterModal();
	};

	const getActiveFiltersCount = () => {
		let count = 0;
	
		// Check for price range filter
		if (tempPriceRangeAnnual[0] !== filterVariables.minAnnualLimit || tempPriceRangeAnnual[1] !== filterVariables.maxAnnualLimit) count++;
		if (tempPriceRangeLifetime[0] !== filterVariables.minLifetimeLimit || tempPriceRangeLifetime[1] !== filterVariables.maxLifetimeLimit) count++;
	
		// Check for insurance provider filter
		if (filters.selectedProviders.length > 0) count++;
	
		// Check for value type filter
		if (selectedValueType) count++;

		if (filters.preHospitalisation) count++;

		if (filters.postHospitalisation) count++;
	
		return count;
	};

	const toggleProvider = (provider) => {
		setTempSelectedProviders((prev) => {
			const newProviders = prev.includes(provider) 
				? prev.filter((p) => p !== provider)
				: [...prev, provider];
			return newProviders;
		});
	};

	const resetFilter = (filterType) => {
		switch(filterType) {
			case 'annual':
				setFilters(prev => ({
					...prev,
					annualLimitRange: ['', '']
				}));
				setTempPriceRangeAnnual([filterVariables.minAnnualLimit, filterVariables.maxAnnualLimit])
				break;
			case 'lifetime':
				setFilters(prev => ({
					...prev,
					lifetimeLimitRange: ['', '']
				}));
				setTempPriceRangeLifetime([filterVariables.minLifetimeLimit, filterVariables.maxLifetimeLimit]);
				break;
			case 'providers':
				setFilters(prev => ({
					...prev,
					selectedProviders: []
				}));
				setTempSelectedProviders([]);
				break;
			case 'preHospitalisation':
				setFilters(prev => ({
					...prev,
					preHospitalisation: ""
				}));
				break;
			case 'postHospitalisation':
				setFilters(prev => ({
					...prev,
					postHospitalisation: ""
				}));
				break;
			default:
				break;
		}
	};

	const renderPlans = () => {
		if (!plans.length) {
			return (
				<div className="no-quotes-message text-center py-4">
					<h4>{`${t("healthInsurance.selectHealthPlan.noQuoteAvailable")}`}</h4>
				</div>
			);
		}

		return plans.map((pagePlan, index) => (
			<div
				key={index}
				ref={index === plans.length - 1 ? lastPlanElementRef : null}
				className="car-plan-card border-radius-25 p-0 m-0"
			>
				{pagePlan.isRecommended && (
					<div className="recommended-badge fw-medium">{`${t("common.recommended")}`}</div>
				)}
	
				<div className="w-100 py-6 px-5">
					<div className="d-flex flex-row input-group align-items-center gap-4 mb-0">
						<RadioButton
							inputId="insurance"
							name="insurance"
							value={pagePlan.id}
							onChange={() => setPlan(pagePlan)}
							checked={pagePlan.id === plan?.id}
						/>
						<div className="w-50">
							<Image 
								width="100%" 
								height="auto" 
								src={pagePlan.image_url} 
								alt={pagePlan.company_name}
								style={{
									maxWidth: "150px", 
									height: "auto",
									display: "block",
									margin: "0 auto"
								}}
							/>
						</div>
					</div>

					<div className="d-flex flex-row input-group align-items-center gap-4 my-2">
						<span className="fw-bold">{pagePlan.plan_name}</span>
					</div>

					<div className="py-2">
						<div className="d-flex flex-row align-items-center gap-2">
							<FaCheck className="fill-dark"/>
							<span className="fw-bold w-85">{`${t("healthInsurance.selectHealthPlan.includeRoomAndBoard")} RM ${quoteHealthDetails.room_board} ${t("healthInsurance.selectHealthPlan.perNight")}`}</span>
						</div>
					</div>

					<div className="py-2">
						<div className="d-flex flex-row align-items-center gap-2">
							<FaCheck className="fill-dark"/> 
							<span className="fw-bold">{`${t("healthInsurance.selectHealthPlan.preAndPostHospitalisation")}:`}</span>
							<ul className="my-0">
								<li>{`${pagePlan.pre_hospitalisation} ${t("healthInsurance.selectHealthPlan.daysForPreHospitalisation")}`}</li>
								<li>{`${pagePlan.post_hospitalisation} ${t("healthInsurance.selectHealthPlan.daysForPostHospitalisation")}`}</li>
							</ul>
						</div>
					</div>

					<div className="py-2">
						<div className="d-flex flex-row align-items-center gap-2">
							<FaCheck className="fill-dark"/> 
							<span className="fw-bold">{`${t("healthInsurance.selectHealthPlan.fullReimbursementFor")}:`}</span>
							<ul className="my-0">
								{pagePlan.kidney_dialysis === "Full Reimbursement" && (
									<li>{`${t("healthInsurance.selectHealthPlan.outPatientKidneyDialysisTreatment")}`}</li>
								)}
								{pagePlan.cancer_treatment === "Full Reimbursement" && (
									<li>{`${t("healthInsurance.selectHealthPlan.outPatientCancerTreatment")}`}</li>
								)}
							</ul>
						</div>
					</div>

					<div className="py-2">
						<div className="d-flex flex-row align-items-center gap-2">
							<FaCheck className="fill-dark"/> 
							<span className="fw-bold">{`Limits:`}</span>
							<ul className="my-0">
								<li>{`${t("common.upTo")} RM ${pagePlan.annual_limit} ${t("common.annualLimit")}`}</li>
								<li>
									{pagePlan.lifetime_limit === "0.00"
										? `${t("common.noLifetimeLimit")}`
										: `${t("common.upTo")} RM ${pagePlan.lifetime_limit} ${t("common.lifetimeLimit")}`}
								</li>
							</ul>
						</div>
					</div>

					<div className="mt-4">
						<span 
							className="fw-bold"
							style={{
								color: "red"
							}}
						>
							{`Excess / Deductible RM ${new Intl.NumberFormat("en-MY", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							}).format(pagePlan?.excess || 0)}`}
						</span>
					</div>

					<div className="mt-4">
						<span className="fs-xl fw-bold">{`RM ${pagePlan.premium}`}</span>
						<span className="formatted-text"> / {`${t("healthInsurance.selectHealthPlan.filters.perAnnual")}`}</span>
					</div>
				</div>
			</div>
		));
	};	

	return (
		<>
		{loading && <Loader />}
		<div className="select-health-plan-page">
			<div className="content">
				<div className="get-quote-process-container">
					<div className="d-flex w-100">
						<div className="d-flex flex-column">
							<h3 className="mb-0">{`${t("healthInsurance.selectHealthPlan.title")}`}</h3>
							<p className="formatted-text">
							{`${t("healthInsurance.selectHealthPlan.subTitle")}`}
							</p>
						</div>

						<TabView
							activeIndex={activeTabIndex}
							onTabChange={(e) => handleSwitchTab(e)}
							className={`${plan ? 'mb-300' : ''} w-100`}
						>
							<TabPanel header={`${t("common.allInsurance")}`}>
								<div
									className="filter d-flex flex-row align-items-center bg-light-blue p-10 mb-2"
									onClick={openFilterModal}
								>
									<i className="pi pi-filter-fill pr-10"></i>{" "}
									<span className="pr-20">{`${t("common.filterBy")}`}</span>
										{getActiveFiltersCount() > 0 && (
											<span className="filter-badge">{getActiveFiltersCount()}</span>
										)}
									<i className="pi pi-angle-down"></i>
								</div>

								<Dialog
									className="filter-modal d-flex flex-row bg-white p-40 gap-2"
									modal
									header={`Filters`}
									visible={isModalVisible}
									style={{
										borderRadius: "24px 24px 0 0",
										margin: 0,
										width: "100vw",
										overflowY: "auto",
									}}
									position={"bottom"}
									onHide={closeFilterModal}
									content={({ hide }) => (
										<>
											<div className="filters w-100">
												<div className="d-flex justify-content-between mb-4">
													<div style={{ maxWidth: '80%' }}>
														<h2 className="m-0">{`${t("common.filters")}`}</h2>
														<span className="formatted-text">{`${t("healthInsurance.selectHealthPlan.filters.subTitle")}`}</span>
													</div>
													<div style={{ maxWidth: '20%' }} onClick={closeFilterModal}>
														<i className="pi pi-times-circle color-primary"></i>
														{` `}
														<span className="color-primary">{`${t("common.close")}`}</span>
													</div>
												</div>
												{/* Filter Content */}
												<div className="filters">
													{/* Insurance Provider Filter */}
													<div className="mb-4">
														<h4>{`${t("healthInsurance.selectHealthPlan.filters.insuranceProvider")}`}</h4>
														<div className="providers d-flex flex-wrap gap-2">
															{filterVariables.providers.map((company_name, index) => (
																<div
																	key={index}
																	className={`provider-btn ${
																		tempSelectedProviders.includes(company_name)
																			? "active"
																			: ""
																	}`}
																	onClick={() => toggleProvider(company_name)}
																>
																	{company_name}
																</div>
															))}
														</div>
													</div>

													{/* Hospitalisation Filter */}
													<div className="row justify-content-between mx-0">
														<div className="col-lg-6 col-sm-6 input-group text-left">
															<h4>{`${t("healthInsurance.selectHealthPlan.filters.preHospitalisation")}`}&nbsp;</h4>
															<div className="d-flex flex-column gap-3 justify-content-start align-items-start">
															<FloatingSelect
																	options={hospitalisationOptions}
																	value={filters.preHospitalisation}
																	onChange={handlePreHospitalisationSelectChange}
																/>
															</div>
														</div>

														<div className="col-lg-6 col-sm-6 input-group text-left">
														<h4>{`${t("healthInsurance.selectHealthPlan.filters.postHospitalisation")}`}</h4>
															<div className="d-flex flex-column gap-3 justify-content-start align-items-start">
																<FloatingSelect
																	options={hospitalisationOptions}
																	value={filters.postHospitalisation}
																	onChange={handlePostHospitalisationSelectChange}
																/>
															</div>
														</div>
													</div>

													{/* Price Range Filter */}
													<div className="mb-4">
														<h4 className="mb-2">{`${t("common.annualLimit")}`}</h4>
														<div className="slider-labels d-flex justify-content-between">
															<span className="price-label">{`${t("common.from")}`}</span>
															<span className="price-label">{`${t("common.to")}`}</span>
														</div>
														<div className="slider-container">
															<Slider
																value={tempPriceRangeAnnual}
																onChange={handleSliderChangeAnnual}
																min={filterVariables.minAnnualLimit}
																max={filterVariables.maxAnnualLimit}
																step={100}
																sx={{
																	color: "#0B4FE4",
																	height: 8,
																	'& .MuiSlider-thumb': {
																		height: 24,
																		width: 24,
																		border: '2px solid',
																	},
																	'& .MuiSlider-rail': {
																			opacity: 0.3,
																		height: 8,
																	},
																}}
															/>
														</div>
														<div className="price-range-display mt-2">
															<span>{`RM ${tempPriceRangeAnnual[0]} - RM ${tempPriceRangeAnnual[1]}`}</span>
														</div>
													</div>

													{/* Price Range Filter */}
													<div className="mb-4">
														<h4 className="mb-2">{`${t("common.lifetimeLimit")}`}</h4>
														<div className="slider-labels d-flex justify-content-between">
															<span className="price-label">{`${t("common.from")}`}</span>
															<span className="price-label">{`${t("common.to")}`}</span>
														</div>
														<div className="slider-container">
															<Slider
																value={tempPriceRangeLifetime}
																onChange={handleSliderChangeLifetime}
																min={filterVariables.minLifetimeLimit}
																max={filterVariables.maxLifetimeLimit}
																step={100}
																sx={{
																	color: "#0B4FE4",
																	height: 8,
																	'& .MuiSlider-thumb': {
																		height: 24,
																		width: 24,
																		border: '2px solid',
																	},
																	'& .MuiSlider-rail': {
																		opacity: 0.3,
																		height: 8,
																	},
																}}
															/>
														</div>
														<div className="price-range-display mt-2">
															<span>{`RM ${tempPriceRangeLifetime[0]} - RM ${tempPriceRangeLifetime[1]}`}</span>
														</div>
													</div>
												</div>
											</div>
											<Button
												className="btn-primary"
												label={`${t("common.applyFilter")}`}
												onClick={handleApplyFilter}
											/>
										</>
									)}
								></Dialog>
								<div className="tag-group d-flex flex-row gap-2 mb-2">
									{tagsVisible && (
										<>
											{Array.isArray(tempPriceRangeAnnual) &&
												tempPriceRangeAnnual.length === 2 &&
												(tempPriceRangeAnnual[0] !== filterVariables.minAnnualLimit || tempPriceRangeAnnual[1] !== filterVariables.maxAnnualLimit) && (
													<div className="tags bg-primary p-10 border-radius-5">
													<span>{`${t("annualLimit")} - RM${tempPriceRangeAnnual[0]} - RM ${tempPriceRangeAnnual[1]}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('annual')}
													></i>
													</div>
											)}

											{Array.isArray(tempPriceRangeLifetime) &&
												tempPriceRangeLifetime.length === 2 &&
												(tempPriceRangeLifetime[0] !== filterVariables.minLifetimeLimit || tempPriceRangeLifetime[1] !== filterVariables.maxLifetimeLimit) && (
													<div className="tags bg-primary p-10 border-radius-5">
													<span>{`${t("lifetimeLimit")} - RM${tempPriceRangeLifetime[0]} - RM ${tempPriceRangeLifetime[1]}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('lifetime')}
													></i>
													</div>
											)}

											{filters.selectedProviders.length > 0 && (
												<div className="tags bg-primary p-10 border-radius-5">
													<span>{`${t("common.insurance")} - ${filters.selectedProviders}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('providers')}
													></i>
												</div>
											)}

											{filters.preHospitalisation && (
												<div className="tags bg-primary p-10 border-radius-5">
													<span>{`${t("healthInsurance.selectHealthPlan.filters.preHospitalisation")} - ${filters.preHospitalisation} ${t("healthInsurance.selectHealthPlan.filters.days")}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('preHospitalisation')}
													></i>
												</div>
											)}

											{filters.postHospitalisation && (
												<div className="tags bg-primary p-10 border-radius-5">
													<span>{`${t("healthInsurance.selectHealthPlan.filters.postHospitalisation")} - ${filters.postHospitalisation} ${t("healthInsurance.selectHealthPlan.filters.days")}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('postHospitalisation')}
													></i>
												</div>
											)}
										</>
									)}
								</div>
								<div className="plan-grid d-flex justify-content-center gap-4">
									{renderPlans()}
									{isLoading && (
										<div className="text-center py-4">
											<i className="pi pi-spinner pi-spin"></i>
											<span className="ml-2">{`${t("healthInsurance.selectHealthPlan.loadingMorePlans")}`}</span>
										</div>
									)}
								</div>

								{/* <div className="pagination d-flex flex-row justify-content-around align-items-center mt-3">
									{data && data.hasPreviousPage && 
										<Button 
											onClick={() => handlePageChange(filters.currentPage - 1)}
											icon="pi pi-angle-left"
										/>
									}
									<span>Page {filters.currentPage} of {data.totalPages}</span>
									{data && data.hasNextPage && 
										<Button
											onClick={() => handlePageChange(filters.currentPage + 1)}
											icon="pi pi-angle-right"
										/>
									}
								</div> */}
							</TabPanel>

							{/* <TabPanel header="Takaful Insurance">
								<div
									className="filter d-flex flex-row align-items-center bg-light-blue p-10 mb-2"
									onClick={openFilterModal}
								>
									<i className="pi pi-filter-fill pr-10"></i>{" "}
									<span className="pr-20">{"Filter by"}</span>
										{getActiveFiltersCount() > 0 && (
											<span className="filter-badge">{getActiveFiltersCount()}</span>
										)}
									<i className="pi pi-angle-down"></i>
								</div>

								<Dialog
									className="filter-modal d-flex flex-row bg-white p-40 gap-2"
									modal
									header={`Filters`}
									visible={isModalVisible}
									style={{
										borderRadius: "24px 24px 0 0",
										margin: 0,
										width: "100vw",
										overflowY: "auto",
									}}
									position={"bottom"}
									onHide={closeFilterModal}
									content={({ hide }) => (
										<>
											<div className="filters w-100">
												<div className="mb-4">
													<h2 className="m-0">{`Filters`}</h2>
													<span className="formatted-text">{`You’re able to select multiple selection from the list`}</span>
												</div> */}
												{/* Filter Content */}
												{/* <div className="filters"> */}
													{/* Insurance Provider Filter */}
													{/* <div className="mb-4">
														<h4>{`Insurance Provider`}</h4>
														<div className="providers d-flex flex-wrap gap-2">
															{filterVariables.providers.map((provider, index) => (
																<div
																	key={index}
																	className={`provider-btn ${
																		tempSelectedProviders.includes(provider)
																			? "active"
																			: ""
																	}`}
																	onClick={() => toggleProvider(provider)}
																>
																	{provider}
																</div>
															))}
														</div>
													</div> */}

													{/* Price Range Filter */}
													{/* <div className="mb-4">
														<h4 className="mb-2">{`Annual Limit`}</h4>
														<div className="slider-labels d-flex justify-content-between">
															<span className="price-label">{`${t("common.from")}`}</span>
															<span className="price-label">{`${t("common.to")}`}</span>
														</div>
														<div className="slider-container">
															<Slider
																value={tempPriceRangeAnnual}
																onChange={handleSliderChangeAnnual}
																min={filterVariables.minAnnualLimit}
																max={filterVariables.maxAnnualLimit}
																step={100}
																sx={{
																	color: "#0B4FE4",
																	height: 8,
																	'& .MuiSlider-thumb': {
																		height: 24,
																		width: 24,
																		border: '2px solid',
																	},
																	'& .MuiSlider-rail': {
																		opacity: 0.3,
																		height: 8,
																	},
																}}
															/>
														</div>
														<div className="price-range-display mt-2">
															<span>{`RM ${tempPriceRangeAnnual[0]} - RM ${tempPriceRangeAnnual[1]}`}</span>
														</div>
													</div>
												</div>
											</div>
											<Button
												className="btn-primary"
												label="Apply Filter"
												onClick={handleApplyFilter}
											/>
										</>
									)}
								></Dialog>
								<div className="tag-group d-flex flex-row gap-2 mb-2">
									{tagsVisible && (
										<>
											{Array.isArray(tempPriceRangeAnnual) &&
												tempPriceRangeAnnual.length === 2 &&
												(tempPriceRangeAnnual[0] !== filterVariables.minAnnualLimit || tempPriceRangeAnnual[1] !== filterVariables.maxAnnualLimit) && (
													<div className="tags bg-primary p-10 border-radius-5">
													<span>{`Annual Limit - RM${tempPriceRangeAnnual[0]} - RM ${tempPriceRangeAnnual[1]}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('annual')}
													></i>
													</div>
											)}

											{filters.selectedProviders.length > 0 && (
												<div className="tags bg-primary p-10 border-radius-5">
													<span>{`Insurance - ${filters.selectedProviders}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => resetFilter('providers')}
													></i>
												</div>
											)}

											{selectedValueType && (
												<div className="tags bg-primary p-10 border-radius-5">
													<span>{`Value - ${selectedValueType}`}</span>
													<i
														className="pi pi-times color-light ml-2 cursor-pointer"
														onClick={() => {
															setSelectedValueType(null);
															setFilters(prev => ({...prev}));
														}}
													></i>
												</div>
											)}
										</>
									)}
								</div>
								<div className="plan-grid d-flex justify-content-center gap-4">
									{renderPlans()}
								</div>
							</TabPanel> */}
						</TabView>

						{plan && (
							<div className="sticky-bottom"
								style={{ display: isModalVisible ? "none" : "block" }}
							>
								<div className="d-flex flex-row flex-wrap justify-content-between align-items-center plan-details-section">
									<div className="w-50">
										<span className="formatted-text">{`${t("healthInsurance.selectHealthPlan.filters.selectingPlanFor")}`}</span>
										<div className="fw-bold">{plan.plan_name}</div>
									</div>

									<div className="w-50">
										<Image 
											width="100%" 
											height="auto" 
											src={plan.image_url} 
											alt={plan.company_name}
											style={{
												maxWidth: "120px", 
												height: "auto",
												display: "block",
												margin: "0 auto"
											}}
										/>
									</div>
								</div>
								<hr></hr>

								<div className="d-flex flex-row flex-wrap justify-content-between align-items-center">
									<div className="my-4">
										<span className="fs-xl fw-bold">
											{`RM ${plan.premium}`}
										</span>
										<span>{` / ${t("healthInsurance.selectHealthPlan.filters.perAnnual")}`}</span>
									</div>
								</div>

								<Button
									// label="Proceed (1/7)"
									label={`${t("common.proceed")} (1/7)`}
									className="btn-primary"
									onClick={submitSelectedPlan}
								/>

								{/* BottomMessagePopUp Component */}
								<BottomMessagePopUp
									message={errorMessage}
									type="error"
									isVisible={isErrorVisible}
									onClose={() => setIsErrorVisible(false)}
								/>
							</div>
						)}
					</div>
				</div>
			</div>
			</div>
		</>
	);
};

export default SelectHealthPlan;